import { useMemo, useState } from "react";
import { t } from "ttag";

import { FieldPanel } from "metabase/querying/fields/components/FieldPanel";
import { Button, Group } from "metabase/ui";
import type * as Lib from "metabase-lib";
import type Question from "metabase-lib/v1/Question";
import type {
  DatasetColumn,
  TableColumnOrderSetting,
  VisualizationSettings,
} from "metabase-types/api";

import { TableColumnPanel } from "./TableColumnPanel";
import {
  reorderColumnSettingsToQueryOrder,
  reorderColumnWidthsToMatchQueryOrder,
} from "./TableColumnPanel/utils";
import type { EditWidgetData } from "./types";
import { canEditQuery } from "./utils";

interface ChartSettingTableColumnsProps {
  value: TableColumnOrderSetting[];
  columns: DatasetColumn[];
  columnWidths?: number[];
  question?: Question;
  isShowingDetailsOnlyColumns: boolean;
  getColumnName: (column: DatasetColumn) => string;
  onChange: (value: TableColumnOrderSetting[], question?: Question) => void;
  onChangeSettings?: (settings: Partial<VisualizationSettings>) => void;
  onShowWidget: (config: EditWidgetData, targetElement: HTMLElement) => void;
}

export const ChartSettingTableColumns = ({
  value,
  columns,
  columnWidths,
  question,
  isShowingDetailsOnlyColumns,
  getColumnName,
  onChange,
  onChangeSettings,
  onShowWidget,
}: ChartSettingTableColumnsProps) => {
  const query = question?.query();
  const stageIndex = -1;
  const hasEditButton = canEditQuery(query);
  const [isEditingQuery, setIsEditingQuery] = useState(false);
  const reorderedColumnSettings = useMemo(
    () => reorderColumnSettingsToQueryOrder(columns, value),
    [columns, value],
  );
  const canResetToQueryOrder = useMemo(() => {
    if (value.length !== reorderedColumnSettings.length) {
      return true;
    }

    return value.some(
      (setting, index) => setting.name !== reorderedColumnSettings[index]?.name,
    );
  }, [reorderedColumnSettings, value]);

  const handleQueryChange = (query: Lib.Query) => {
    onChange(value, question?.setQuery(query));
  };

  const handleResetToQueryOrder = () => {
    const reorderedColumnWidths = reorderColumnWidthsToMatchQueryOrder(
      value,
      columnWidths,
      reorderedColumnSettings,
    );

    onChangeSettings?.({
      "table.columns": reorderedColumnSettings,
      "table.column_widths": reorderedColumnWidths,
    });

    if (!onChangeSettings) {
      onChange(reorderedColumnSettings);
    }
  };

  return (
    <div>
      {(hasEditButton || canResetToQueryOrder) && (
        <Group gap="md" mb="sm">
          {hasEditButton && (
            <Button
              pl="0"
              variant="subtle"
              onClick={() => setIsEditingQuery(!isEditingQuery)}
            >
              {isEditingQuery
                ? t`Done picking columns`
                : t`Add or remove columns`}
            </Button>
          )}
          {canResetToQueryOrder && !isEditingQuery && (
            <Button pl="0" variant="subtle" onClick={handleResetToQueryOrder}>
              {t`Reset to query order`}
            </Button>
          )}
        </Group>
      )}
      {query != null && isEditingQuery ? (
        <FieldPanel
          query={query}
          stageIndex={stageIndex}
          onChange={handleQueryChange}
        />
      ) : (
        <TableColumnPanel
          columns={columns}
          columnSettings={value}
          isShowingDetailsOnlyColumns={isShowingDetailsOnlyColumns}
          getColumnName={getColumnName}
          onChange={onChange}
          onShowWidget={onShowWidget}
        />
      )}
    </div>
  );
};
